import { Inject, Injectable } from '@angular/core';
import { WINDOW_INJECTION_TOKEN } from '../tokens/window.injection.token';

export enum ACCESS_KEYS {
    AUTH_TOKEN = 'authToken',
    BLUMEPRESS_ID = 'blumepressId',
    CURRENT_PRESSROOM = 'Active-Pressroom',
    CURRENT_PRESSROOM_PATH = 'Active-Pressroom-Path',
    CURRENT_ACCOUNT = 'Active-Account',
    WEB_TAB = 'pressConfigTabLocation',
    /**
     * Per-user shell preference for the Pressroom Design editor.
     * Values: '0' = legacy tab UI, '1' or unset = new shell (default).
     * Toggled by URL params `?newShell=1` / `?newShell=0` or in-app switch buttons.
     */
    WEBCONFIG_NEW_SHELL = 'webconfig:newShell',
}

@Injectable({
    providedIn: 'root',
})
export class LocalStorageService {
    constructor(
        @Inject(WINDOW_INJECTION_TOKEN) private readonly window: Window,
    ) {}

    get ACCESS_KEYS(): typeof ACCESS_KEYS {
        return ACCESS_KEYS;
    }

    getItem(key: ACCESS_KEYS): string {
        return this.window.localStorage.getItem(key) || '';
    }

    setItem(key: ACCESS_KEYS, value: string): void {
        this.window.localStorage.setItem(key, value);
    }

    /**
     * Stores a value, coercing null/undefined (and the strings "undefined"/"null" that result
     * from stringifying them) to ''. Prevents the literal "undefined" from being written —
     * which previously poisoned the pressroom path and broke live preview / open-site URLs.
     */
    setItemSafe(key: ACCESS_KEYS, value: unknown): void {
        const str = value === null || value === undefined ? '' : String(value);
        this.window.localStorage.setItem(
            key,
            str === 'undefined' || str === 'null' ? '' : str,
        );
    }

    removeItem(key: ACCESS_KEYS): void {
        this.window.localStorage.removeItem(key);
    }

    /**
     * Resolves the URL segment for the live pressroom site. Prefers the path slug, but falls
     * back to the pressroom ID — the public/livesite endpoints resolve a pressroom by either
     * its `_id` or its `path`, so a missing/blank/"undefined" path never yields a broken URL.
     * Returns '' only when neither value is usable.
     */
    getLivePressroomSegment(): string {
        const invalid = ['', 'undefined', 'null', 'noPressroom'];
        const path = this.getItem(ACCESS_KEYS.CURRENT_PRESSROOM_PATH);
        if (path && !invalid.includes(path)) {
            return path;
        }
        const id = this.getItem(ACCESS_KEYS.CURRENT_PRESSROOM);
        if (id && !invalid.includes(id)) {
            return id;
        }
        return '';
    }
}
